import {Component, OnInit, Input, OnChanges} from '@angular/core';
import { OptionService } from "../option/option.service";
import { getIconForPaymentMethod, getCreditCardTitles } from './payment.helper';
import {TrackingComponent} from "../tracking/tracking.component";
import {PaymentComponent} from "./payment.component";

interface PaymentPreviewItem {
    icon: string;
    class: string;
    label: string;
    translation: string;
}

@Component({
    selector: 'payment-preview',
    templateUrl: './payment.preview.component.html'
})
export class PaymentPreviewComponent implements OnInit, OnChanges {
    /**
     * version of the visualisation
     *
     * @type {String}
     */
    @Input() version: string;

    /**
     * Size of the users browser
     */
    @Input() size: string;

    /**
     * current step
     */
    @Input() step: string;

    /**
     * object of available payment method icons
     */
    paymentMethodIcons: PaymentPreviewItem[];

    /**
     * flag to hide payment icons
     */
    hasHidePaymentIcons = true;

    /**
     * The constructor
     * @param optionService
     */
    constructor(private optionService: OptionService) { }

    /**
     * init
     * @returns {void}
     */
    ngOnInit(): void {
        // check visibility
        this.checkVisibility();

        // get activated donation rhythms
        const donationRythm = this.optionService.getOption('donation_rhythm');
        const hasOneTime = donationRythm["one_time"] === true;
        const hasRecurring = donationRythm["yearly"] === true || donationRythm["halfyearly"] === true ||
          donationRythm["quarterly"] === true || donationRythm["monthly"] === true;

        // get all activated payment methods
        let paymentMethods = this.optionService.getPaymentMethods() as Array<string>;

        // filter some payment methods
        paymentMethods = paymentMethods.filter(method => {
          if (hasRecurring === false && method.match(/_recurring$/)) {
            return false;
          }
          return !(hasOneTime === false && !method.match(/_recurring$/));
        });

        // map payment to icons and labels
        const mappedPaymentMethods = paymentMethods.map((payment) => {
            // replace _recurring payment methods
            const paymentLabel = payment.replace(/_recurring/, "");
            let iconName = this.getIcon(paymentLabel);
            let translation = 'payment.' + paymentLabel + '.label';

            // if payment is generic
            if (paymentLabel === 'generic') {
                translation = this.getGenericName();

                // if icon is unknown
                if (iconName === 'payment-') {
                    iconName = 'payment-generic';
                }
            }

            // if payment is creditcard/sixpay
            if (paymentLabel.indexOf('creditcard') !== -1 || paymentLabel.indexOf('sixpay') !== -1) {
                translation = getCreditCardTitles(this.optionService);
            }

            return {
                icon: iconName,
                class: 'payment-preview__icon--' + iconName,
                label: paymentLabel,
                translation,
            }
        });

        // Remove duplicate options in the mappedPaymentMehods array
        // but keep one generic payment method if it exists and even if it is a duplicate icon
        const filteredArray = mappedPaymentMethods.reduce((uniqueIcons: Record<string, PaymentPreviewItem>, currentItem) => {
            if (!uniqueIcons[currentItem.icon]) {
                uniqueIcons[currentItem.icon] = currentItem;
            }
            else if (currentItem.label === "generic" || currentItem.label === "payment-generic" && !uniqueIcons.generic) {
                uniqueIcons['payment-generic'] = { ...currentItem, class: 'payment-preview__icon--payment-generic' };
            }
            return uniqueIcons;
        }, {});

        this.paymentMethodIcons = Object.values(filteredArray);

        this.paymentMethodIcons.filter(item => {
          return item.label.match(/(apple|google)_pay/);
          })
          .forEach(method => {
            if (PaymentComponent.isPaymentMethodAvailable(method.label) === false) {
              // remove the icon from the list
              this.paymentMethodIcons = this.paymentMethodIcons.filter(item => item.label !== method.label);
            }
        });
    }

    /**
     * watch for changes
     */
    ngOnChanges(): void {
        this.checkVisibility();
    }

    /**
     * check if payment icons should be visible
     */
    checkVisibility(): void {
        // different checks
        const isInvalidVersion = !(['widget', 'shop', 'giftshop', 'page'].includes(this.version));
        const isPageAndNotSmall = this.version === 'page' && this.size !== 'small';
        // list of steps on which the icons should not be visible
        const hideOnStep = ['payment', 'payment-select', 'end', 'thankyou', 'banktransfer', 'contact'].indexOf(this.step) !== -1;
        const hasHidePaymentIcons = this.getHasHidePaymentIcons();

        // Check if payment icons should be hidden based on conditions
        this.hasHidePaymentIcons = hasHidePaymentIcons || isInvalidVersion || isPageAndNotSmall || hideOnStep;
    }

    /**
     * Returns the name of the icon for a paymentMethod
     * @param paymentMethod
     * @returns {string}
     */
    getIcon(paymentMethod: string): string {
        return getIconForPaymentMethod(paymentMethod, this.optionService);
    }

    /**
     * get name for generic payment method
     */
    getGenericName(): any {
        const genericOptionsHint = this.optionService.getOrganisationData("payment_generic");
        return genericOptionsHint.name;
    }

    /**
     * get the hide payment icons flag
     */
    getHasHidePaymentIcons(): boolean {
        return this.optionService.getOrganisationData('has_hide_paymenticons');
    }

    /**
     * Track the click on the payment icons.
     */
    onClick(): void {
      TrackingComponent.event('click', 'payment-icons', null);
    }
}
